import { type Middleware, combineReducers, configureStore } from '@reduxjs/toolkit';
import { createLogger } from 'redux-logger';
import { persistReducer, persistStore } from 'redux-persist';
import { PersistPartial } from 'redux-persist/es/persistReducer';
import storage from 'redux-persist/lib/storage';
import {
  curriculumReducer,
  dashboardReducer,
  eventReducer,
  examReducer,
  GuardianReducer,
  messageQueueReducer,
  NotificationReducer,
  organizationReducer,
  paymentReducer,
  paymentSessionReducer,
  postReducer,
  syllabusReducer,
  academicYearReducer,
  organizationAdminReducer,
  classroomAssignReducer,
  feeTemplateReducer,
  etlReducer,
  subscriptionReducer,
  notificationLogReducer,
  homeworkReducer,
} from '../slices';
import bulkUploadReducer from '../slices/bulkUpload.slice';
import homeworkDiscussionReducer from '../slices/homeworkDiscussion.slice';
import parentHomeworkReducer from '../slices/parentHomework.slice';
import parentEventReducer from '../slices/parentEvent.slice';
import parentSubjectReducer from '../slices/parentSubject.slice';
import parentTimetableReducer from '../slices/parentTimetable.slice';
import studentPortalExamReducer from '../slices/studentPortalExam.slice';
import examAttemptReducer from '../slices/examAttempt.slice';
import organizationPreferenceReducer from '../slices/organizationPreferenceSlice';
import attendanceReducer from '../slices/attendanceSliceModel';
import authReducer from '../slices/auth.slice';
import gradeReducer from '../slices/gradeSlice';
import bookReducer from '../slices/libraryManagement.slice';
import registrationFormReducer from '../slices/registrationFormSlice';
import studentReducer from '../slices/studentSliceModel';
import subjectReducer from '../slices/subject.slice';
import formReducer from '../slices/teacherFormSliceModel';
import { teacherReducer } from '../slices/teacherSliceModel';
import { listenerMiddleware } from './listener.middleware';
import teacherAttendanceSlice from '../slices/teacherAttendanceSlice';
import adminOrganizationReducer from '../slices/classveew-admin/organization.slice';
import adminStudentReducer from '../slices/classveew-admin/admin-student.slice';
import adminTeacherReducer from '../slices/classveew-admin/admin-teacher.slice';
import adminGuardianReducer from '../slices/classveew-admin/admin-guardian.slice';
import adminNewsFeedReducer from '../slices/classveew-admin/news-feed.slice';
import adminAwardReducer from '../slices/classveew-admin/admin-award.slice';
import enrollmentPolicyReducer from '../slices/enrollmentPolicy.slice';
import reEnrollmentReducer from '../slices/reEnrollment.slice';
import reEnrollmentStudentReducer from '../slices/reEnrollment-student.slice';
import sessionHistoryReducer from '../slices/sessionHistory.slice';
import agentReducer from '../slices/agent.slice';
import holidayReducer from '../slices/holiday.slice';
import newsFeedReducer from '../slices/news-feed.slice';
// Create the logger middleware
const logger = createLogger();

// Configure the store with the logger middleware
const combinedReducers = combineReducers({
  auth: authReducer,
  dashboard: dashboardReducer,
  student: studentReducer,
  grade: gradeReducer,
  attendance: attendanceReducer,
  subject: subjectReducer,
  teacher: teacherReducer,
  form: formReducer,
  registrationFormReducer: registrationFormReducer,
  payment: paymentReducer,
  paymentSession: paymentSessionReducer,
  event: eventReducer,
  guardian: GuardianReducer,
  notification: NotificationReducer,
  books: bookReducer,
  teacherAttendance: teacherAttendanceSlice,
  curriculum: curriculumReducer,
  messageQueue: messageQueueReducer,
  syllabus: syllabusReducer,
  organizationPreferences: organizationPreferenceReducer,
  exam: examReducer,
  organization: organizationReducer,
  post: postReducer,
  teacherAssignClassroom: classroomAssignReducer,
  academicYear: academicYearReducer,
  organizationAdmin: organizationAdminReducer,
  feeTemplate: feeTemplateReducer,
  adminOrganization: adminOrganizationReducer,
  adminStudent: adminStudentReducer,
  adminTeacher: adminTeacherReducer,
  adminGuardian: adminGuardianReducer,
  adminNewsFeed: adminNewsFeedReducer,
  adminAward: adminAwardReducer,
  enrollmentPolicy: enrollmentPolicyReducer,
  reEnrollment: reEnrollmentReducer,
  reEnrollmentStudent: reEnrollmentStudentReducer,
  etl: etlReducer,
  bulkUpload: bulkUploadReducer,
  subscription: subscriptionReducer,
  notificationLog: notificationLogReducer,
  sessionHistory: sessionHistoryReducer,
  agent: agentReducer,
  homework: homeworkReducer,
  homeworkDiscussion: homeworkDiscussionReducer,
  parentHomework: parentHomeworkReducer,
  parentEvent: parentEventReducer,
  parentSubject: parentSubjectReducer,
  parentTimetable: parentTimetableReducer,
  studentPortalExam: studentPortalExamReducer,
  examAttempt: examAttemptReducer,
  holiday: holidayReducer,
  newsFeed: newsFeedReducer,
});

// Redux Persist Configurations
const persistConfig = {
  key: 'root', // Root-level key
  // Exclude from persistence (fetched fresh each session).
  //
  // `exam` is here because persisting it is actively harmful, not merely
  // wasteful: redux-persist's default `autoMergeLevel1` reconciler swaps each
  // persisted slice in WHOLESALE rather than merging it over `initialState`, so
  // a browser holding a blob written before a field was added rehydrates with
  // that field missing entirely — `reusablePapers` came back `undefined` and
  // crashed the paper-source panel on `.map`. Every consumer of this slice
  // (scoreCard, viewExam, reportCard, ExamEventForm, PaperSourceChoice)
  // dispatches its own fetch on mount, so nothing relies on it surviving a
  // reload.
  blacklist: [
    'paymentSession',
    'agent',
    'subscription',
    'exam',
    // Never persist the resolved studentId: a stale one surviving a logout would
    // be an authorization hazard, and both identity and list are cheap to refetch.
    'studentPortalExam',
    // CVA-4197 join-exam plan 0004: confirmed decision — server-only
    // autosave, NO browser-side answer cache. Exam answers are assessed work
    // and student PII, sat on a device a parent may hand to a child; writing
    // them to localStorage is a data-minimisation problem, not just a staleness
    // one. A join/resume call re-fetches everything on mount regardless.
    'examAttempt',
  ],
  storage, // Use localStorage
};

// Apply the persistReducer to the root reducer
const persistedReducer = persistReducer(persistConfig, combinedReducers);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false, // Disable check for non-serializable data like functions
    })
      .concat(logger as Middleware)
      .prepend(listenerMiddleware.middleware),
});

// Create a persistor instance
export const persistor = persistStore(store);

export type RootState = ReturnType<typeof combinedReducers> & PersistPartial;
export type AppDispatch = typeof store.dispatch;
